import * as _ from 'lodash';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import actions from 'src/actions';
import Card from 'src/components/common/Card.redesign';
import CardContainer from 'src/components/common/CardContainer.redesign';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import DialogList from 'src/dialogs';
import PagedResultSet from 'src/models/common/PagedResultSet';
import LeaseGenerationTractDetail from 'src/models/leasing/LeaseGenerationTractDetail';
import { Runsheet } from 'src/models/Runsheets';
import User from 'src/models/User';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import useFetch from 'use-http';
import RunsheetDetailsDialog from './RunsheetDetailsDialog';
import { ODataOptions, ODataOrderByDirection, ODataFilter, ODataFilterOperators } from 'src/util/ODataBuilder';
import { ITableConfig } from 'src/components/common/table';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import { useForm } from 'react-hook-form';
import { push } from 'connected-react-router';
import IndexReferenceType from 'src/models/IndexReferenceType';
import Landman from 'src/models/Landman';
import CurativeRequirement from 'src/models/CurativeRequirement';
import AgreementUtil from 'src/util/AgreementUtil';
// import RunsheetDetail from './RunsheetDetail';

const RunsheetPage = () => {
    const appContext = useContext<IAppContextState>(AppContext);
    const dispatch = useDispatch();
    const { get, response } = useFetch();
    /* Kept separate from the list fetches above so a download can't clobber their shared response. */
    const { get: getExport, response: exportResponse } = useFetch();
    const { register } = useForm();

    const [runsheets, setRunsheets] = useState<Runsheet[]>([]);
    const [selectedRunsheet, setSelectedRunsheet] = useState<Runsheet>(undefined);
    const [selectedTractId, setSelectedTractId] = useState<number>(undefined);
    const [seededTractSearchTerm, setSeededTractSearchTerm] = useState<string>('');
    const [users, setUsers] = useState<User[]>([]);
    const [projectTracts, setProjectTracts] = useState<LeaseGenerationTractDetail[]>([]);
    const [dialogTitle, setDialogTitle] = useState<string>('');
    const [projectTractSearchOptions, setProjectTractSearchOptions] = useState<ODataOptions>(undefined);
    const [runsheetSearchOptions, setRunsheetSearchOptions] = useState<ODataOptions>(undefined);
    // const [vendorSearchTerm, setVendorSearchTerm] = useState<string>(undefined);
    const [fetchNextProjectTractsPage, setFetchNextProjectTractsPage] = useState<boolean>(true);
    const [fetchNextRunsheetsPage, setFetchNextRunsheetsPage] = useState<boolean>(true);
    const [runsheetSearchTerm, setRunsheetSearchTerm] = useState('');
    const [projectTractSearchTerm, setProjectTractSearchTerm] = useState('');
    const [curativeStatuses, setCurativeStatuses] = useState<Array<IndexReferenceType>>([]);
    const [projectStaff, setProjectStaff] = useState<Array<Landman>>([]);
    const [loadingTracts, setLoadingTracts] = useState<boolean>(false);
    const [exportingRoutingLog, setExportingRoutingLog] = useState<boolean>(false);

    useEffect(() => {
        getUsers();
        getStaff();
        getCurativeStatuses();
        initializeProjectTractsSearchSettings();
        initializeRunsheetSearchSettings();

        if (appContext.runsheetTractId !== undefined) {
            getRunsheetAttachedTracts();
        }
    }, []);

    useEffect(() => {
        if (!!selectedRunsheet && projectTractSearchTerm !== undefined) {
            let newprojectTractSearchOptions = _.cloneDeep(projectTractSearchOptions);
            if (projectTractSearchTerm !== '') {
                newprojectTractSearchOptions?.addFilter(new ODataFilter('Tract.TractName', ODataFilterOperators.Contains, projectTractSearchTerm));
            }
            if (!!selectedRunsheet && !!selectedRunsheet?.attachedTractIds) {
                selectedRunsheet.attachedTractIds.forEach(at => {
                    newprojectTractSearchOptions?.addFilter(new ODataFilter('Tract.Id', ODataFilterOperators.Equals, at));
                });
                setProjectTractSearchOptions(newprojectTractSearchOptions);
            }
        }
    }, [selectedRunsheet]);

    useEffect(() => {
        if (!!runsheetSearchTerm || runsheetSearchTerm === '') {
            resetRunsheetSearchOptions();
        }
    }, [runsheetSearchTerm]);

    useEffect(() => {
        if (!!projectTractSearchTerm || projectTractSearchTerm === '') {
            resetProjectTractSearchOptions();
        }
    }, [projectTractSearchTerm]);

    useEffect(() => {
        if (!!runsheetSearchOptions) {
            if (!!runsheetSearchTerm && runsheetSearchTerm.length > 0) {
                if (isNaN(Number(runsheetSearchTerm))) {
                    runsheetSearchOptions?.addFilter(new ODataFilter('WellName', ODataFilterOperators.Contains, runsheetSearchTerm));
                    runsheetSearchOptions?.addFilter(new ODataFilter('FriendlyName', ODataFilterOperators.Contains, runsheetSearchTerm));
                    runsheetSearchOptions?.addFilter(new ODataFilter('TitleAttorney', ODataFilterOperators.Contains, runsheetSearchTerm));
                    runsheetSearchOptions?.addFilter(new ODataFilter('ClientLandman', ODataFilterOperators.Contains, runsheetSearchTerm));
                    runsheetSearchOptions?.addFilter(new ODataFilter('DocumentNumber', ODataFilterOperators.Contains, runsheetSearchTerm));
                } else {
                    // Disabling for now. Will add the ability to search by ID (if desired) later
                    // runsheetSearchOptions?.addFilter(new ODataFilter('Id', ODataFilterOperators.Equals, runsheetSearchTerm));
                    runsheetSearchOptions?.addFilter(new ODataFilter('WellName', ODataFilterOperators.Contains, runsheetSearchTerm));
                    runsheetSearchOptions?.addFilter(new ODataFilter('FriendlyName', ODataFilterOperators.Contains, runsheetSearchTerm));
                    runsheetSearchOptions?.addFilter(new ODataFilter('TitleAttorney', ODataFilterOperators.Contains, runsheetSearchTerm));
                    runsheetSearchOptions?.addFilter(new ODataFilter('ClientLandman', ODataFilterOperators.Contains, runsheetSearchTerm));
                    runsheetSearchOptions?.addFilter(new ODataFilter('DocumentNumber', ODataFilterOperators.Contains, runsheetSearchTerm));
                }
            }
            getRunsheets(null);
        }
    }, [runsheetSearchOptions]);

    useEffect(() => {
        if (!!projectTractSearchOptions) {
            getProjectTracts(!!projectTractSearchTerm ? projectTractSearchTerm : '', null);
        }
    }, [projectTractSearchOptions]);

    const handleTractRunsheet = (attachedRunsheet: Runsheet) => {
        if (attachedRunsheet?.attachedTractIds?.includes(appContext.runsheetTractId)) {
            editRunsheetFromUrl(attachedRunsheet);
        } else {
            CreateRunsheetFromUrl(appContext.runsheetTractId);
        }
    };

    const editRunsheetFromUrl = (attachedRunsheet: Runsheet) => {
        console.log('Edit Runsheet - selected from Tract ', appContext.runsheetTractId);
        setSelectedRunsheet(attachedRunsheet);
        setDialogTitle('Edit Runsheet');
        dispatch(actions.layout.showDialog(DialogList.ADD_RUNSHEET));
    };

    const CreateRunsheetFromUrl = (tractId: number) => {
        const tractIndex = projectTracts.findIndex((t: LeaseGenerationTractDetail) => t.id === tractId );
        setSelectedTractId(tractIndex !== -1 ? tractIndex : undefined);
        getProjectTracts(null, tractId);
        addRunsheet();
    };

    const resetRunsheetSearchOptions = () => {
        if (!!runsheetSearchOptions) {
            setFetchNextRunsheetsPage(true);
            let newRunsheetSearchOptions = _.cloneDeep(runsheetSearchOptions);
            newRunsheetSearchOptions.clearFilters();
            newRunsheetSearchOptions.addPaging(1, 50);
            setRunsheetSearchOptions(newRunsheetSearchOptions);
        }
    };

    const resetProjectTractSearchOptions = () => {
        if (!!projectTractSearchOptions) {
            setFetchNextProjectTractsPage(true);
            let newProjectTractSearchOptions = _.cloneDeep(projectTractSearchOptions);
            newProjectTractSearchOptions.clearFilters();
            newProjectTractSearchOptions.addPaging(1, 50);
            if (projectTractSearchTerm !== '') {
                newProjectTractSearchOptions.addFilter(new ODataFilter('omni', ODataFilterOperators.Contains, projectTractSearchTerm));
            }
            
            setProjectTractSearchOptions(newProjectTractSearchOptions);
        }
    };

    const runsheetSearch = _.debounce((searchContent: string) => {
        setRunsheetSearchTerm(searchContent);
    }, 800);

    const getRunsheets = async (runsheetId: number) => {
        let queryString = !!runsheetSearchOptions ? runsheetSearchOptions?.toString() :
         new ODataOptions().addPaging(1, 50).addOrderBy('Id', ODataOrderByDirection.Ascending).toString();
        if (!!runsheetId) {
            // tslint:disable-next-line:max-line-length
            queryString = new ODataOptions().addPaging(0, Number.MAX_SAFE_INTEGER).addFilter(new ODataFilter('Id', ODataFilterOperators.Equals, runsheetId)).addOrderBy('Id', ODataOrderByDirection.Ascending).addSkipTop(0, Number.MAX_SAFE_INTEGER).toString();
        }
        let data: PagedResultSet<Runsheet> = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/runsheet/by-project${queryString}`);

        if (response.ok) {
            if (!!runsheetSearchOptions && (runsheetSearchOptions?.getPageNumber() > 1) || !!runsheetId) {
                if (!!runsheetId && data?.items?.length === 1) {
                    // const rsIndex = runsheets.findIndex((rs) => rs.id === runsheetId);
                    setSelectedRunsheet(data.items[0]);
                }
                let tempResult = _.cloneDeep(runsheets);
                tempResult = [...tempResult].concat([...data.items]);
                setRunsheets(tempResult);
            } else {
                setRunsheets(data.items);
            }
            setFetchNextRunsheetsPage(data.pageNumber * data.pageSize < data.totalRecords);
        } else {
            console.log('Unable to get Runsheets', response);
        }
        runsheetSearchOptions?.clearFilters();
    };

    const getUsers = async() => {
        let data: PagedResultSet<User> = await get(`v1.0/users`);

        if (response.ok) {
            setUsers(data.items);
        } else {
            console.log('Unable to get Users', response);
        }
    };

    const getStaff = async() => {
        let data: Array<Landman> = await get(`v1.0/landmen/client/${appContext.clientNumber}/project/${appContext.projectNumber}/get-all`);

        if (response.ok) {
            setProjectStaff(data);
        } else {
            console.log('Unable to get Project Staff', response);
        }
    };

    const getCurativeStatuses = async() => {
        let data: Array<IndexReferenceType> = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/runsheet/curative-statuses`);
        
        if (response.ok) {
            setCurativeStatuses(data);
        } else {
            console.log('Unable to get Curative Statuses', response);
        }
    };

    const initializeProjectTractsSearchSettings = () => {
        const tableConfig: ITableConfig = {
            rowCount: 50,
            idField: 'Tract.Number',
            pageNumber: 1,
            columns: []
        };
        setProjectTractSearchOptions(ODataOptions.from(tableConfig).addOrderBy('Tract.Number', ODataOrderByDirection.Ascending));
    };

    const initializeRunsheetSearchSettings = () => {
        const tableConfig: ITableConfig = {
            rowCount: 50,
            idField: 'Tract.Number',
            pageNumber: 1,
            columns: []
        };
        setRunsheetSearchOptions(ODataOptions.from(tableConfig).addOrderBy('Id', ODataOrderByDirection.Ascending));
    };

    const getRunsheetAttachedTracts = async() => {
        let data = await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/runsheet/by-tract/${appContext.runsheetTractId}`);

        if (response.ok) {
            handleTractRunsheet(data);
        } else {
            console.log('Unable to get Runsheet Tract Attachment Mapping.', response);
        }
    };

    const getProjectTracts = async(searchTerm: string, tractId: number) => {
        setLoadingTracts(true);
        // if (!tractId) {
        //     setProjectTractSearchTerm(searchTerm);
        // }
        let queryString = !!projectTractSearchOptions ?
            projectTractSearchOptions?.toString() :
            new ODataOptions().addPaging(1, 50).addOrderBy('Tract.Number', ODataOrderByDirection.Ascending).toString();
        // if (!!tractId)  {
        //     // tslint:disable-next-line:max-line-length
        //     let newDataOptions = new ODataOptions().addPaging(0, Number.MAX_SAFE_INTEGER).addOrderBy('Tract.Id', ODataOrderByDirection.Ascending).addSkipTop(0, Number.MAX_SAFE_INTEGER);
        //     newDataOptions.addFilter(new ODataFilter('exclusive', ODataFilterOperators.Equals, 'exclusive'));
        //     queryString = newDataOptions.toString();
        // }
        let runsheetId = selectedRunsheet?.id || 0;

        let data: PagedResultSet<LeaseGenerationTractDetail> = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.fieldNumber}/tracts/for-runsheet/${runsheetId}/${queryString}`);

        if (response.ok) {
            if (!!projectTractSearchOptions && (projectTractSearchOptions?.getPageNumber() > 1) || !!tractId) {
                let tempResult = _.cloneDeep(projectTracts);
                tempResult = [...tempResult].concat([...data.items]);
                setProjectTracts(tempResult);
            } else {
                setProjectTracts(data.items);
            }
            if (!tractId) {
                setFetchNextProjectTractsPage(data.pageNumber * data.pageSize < data.totalRecords);
            } else {
                setSeededTractSearchTerm(data.items[0].tractName);
            }
            setLoadingTracts(false);
        } else {
            console.log('Unable to get Project Tracts', response);
            setLoadingTracts(false);
        }
    };

    const editRunsheet = (index: number) => {
        setSelectedRunsheet(runsheets[index]);
        setDialogTitle('Edit Runsheet');
        dispatch(actions.layout.showDialog(DialogList.ADD_RUNSHEET));
        // Set up Dialog for Editing.
        // this.props.navigate(`/admin/edit-runsheet/${runsheetId}`);
    };

    const exportRoutingLog = async () => {
        setExportingRoutingLog(true);
        try {
            // tslint:disable-next-line:max-line-length
            await getExport(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/runsheet/routing-log/report`);

            if (!exportResponse.ok) {
                const message = await exportResponse.text();
                alert(message || 'Failed to generate the Runsheet Routing Log.');
                return;
            }

            const blob = await exportResponse.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `RunsheetRoutingLog_${appContext.clientNumber}_${appContext.projectNumber}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            alert('The Runsheet Routing Log could not be downloaded. Please try again or contact support.');
        } finally {
            setExportingRoutingLog(false);
        }
    };

    const addRunsheet = () => {
        setSelectedRunsheet(new Runsheet());
        setDialogTitle('Add Runsheet');
        dispatch(actions.layout.showDialog(DialogList.ADD_RUNSHEET));
    };

    const normalizeUrlPath = () => {
        let currentUrlPath = window?.location?.pathname;
        if (!!currentUrlPath) {
            const urlPathTokens = currentUrlPath.split('/');
            const lastPathToken = urlPathTokens.slice(-1)[0];
            if (lastPathToken !== 'runsheets') {
                dispatch(push(`/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/runsheets`));
            }
        }
    };

    const hideDialog = () => {
        setSelectedTractId(undefined);
        setProjectTractSearchTerm('');
        setSeededTractSearchTerm(undefined);
        normalizeUrlPath();
        resetRunsheetSearchOptions();
        resetProjectTractSearchOptions();
        dispatch(actions.layout.hideDialog());
    };

    const displayRunsheets = () => {
        if (runsheets && runsheets?.length > 0) {
            return runsheets.map((runsheet: Runsheet, index: number) => {
                return (
                    <Card
                        actionPanelStyle={{transform: 'translateY(-.8rem) rotate(90deg)'}}
                        style={{margin: '0'}}
                        collapse={true}
                        key={index}
                        badge={`Runsheet ${runsheet.friendlyName}`}
                        actions={[{ text: 'Edit Runsheet', action: () => editRunsheet(index) }]}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div style={{width: '25%'}}>{runsheet.titleAttorney?.length > 0 ? runsheet.titleAttorney : 'N/A'}</div>
                            <div style={{width: '25%'}}>{runsheet.clientLandman?.length > 0 ? runsheet.clientLandman : 'N/A'}</div>
                            <div style={{width: '25%'}}>{runsheet.wellName?.length > 0 ? runsheet.wellName : 'N/A'}</div>
                            <div style={{width: '25%'}}>{runsheet.documentNumber?.length > 0 ? runsheet.documentNumber : 'N/A'}</div>
                        </div>
                    </Card>
                );
            });
        } else {
            return <div>No Runsheets available</div>;
        }
    };

    const incrementProjectTracts = () => {
        if (!!fetchNextProjectTractsPage) {
            let newSearchOptions = _.cloneDeep(projectTractSearchOptions);
            newSearchOptions.incrementPageNumber();
            setProjectTractSearchOptions(newSearchOptions);
        }
    };

    const incrementRunsheets = () => {
        if (!!fetchNextRunsheetsPage) {
            let newSearchOptions = _.cloneDeep(runsheetSearchOptions);
            newSearchOptions.incrementPageNumber();
            setRunsheetSearchOptions(newSearchOptions);
        }
    };

    const getRunsheetListScrollStatus = (divElement: HTMLDivElement) => {
        if (divElement) {
            const scrollTop = divElement.scrollTop % 1 > .9 ? divElement.scrollTop : Math.round(divElement.scrollTop);
            const scrollHeight = divElement.scrollHeight;
            const clientHeight = divElement.clientHeight;

            // Check if reached the end of the list
            if (scrollTop + clientHeight >= scrollHeight) {
                incrementRunsheets();
                console.log('end of scroll YES');
            } else {
                console.log('end of scroll NO');
            }
        }
    };

    const navigateToTractDetail = (tractId: number) => {
        const tract = projectTracts.find((t) => t.id === tractId);
        if (!!tract) {
            hideDialog();
            dispatch(push(`/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${tract.sectionNumber}/tracts/${tract.tractNumber}/horizons/${tract.horizonNumber}`));
        }
    };

    const finalizeData = (data: Runsheet): Runsheet => {
        if (data !== undefined) {
            data.id = Number(selectedRunsheet?.id || 0);
            data.projectId = Number(appContext.projectNumber);
    
            data.friendlyName = data.friendlyName ? data.friendlyName : '';
            data.titleAttorney = data.titleAttorney ? data.titleAttorney : '';
            data.clientLandman = data.clientLandman ? data.clientLandman : '';
            data.wellName = data.wellName ? data.wellName : '';
            data.documentNumber = data.documentNumber ? data.documentNumber : '';
            data.comments = data.comments ? data.comments : '';
            data.statusTypeCheck = data.statusTypeCheck ? data.statusTypeCheck : '';
    
            data.receivedFromClientUserId = data.receivedFromClientUserId && data.receivedFromClientUserId > 0 ? parseInt(data.receivedFromClientUserId + '', 10) : null;
            data.assignedToLandmanUserId = data.assignedToLandmanUserId && data.assignedToLandmanUserId > 0 ? parseInt(data.assignedToLandmanUserId + '', 10) : null;
            data.receivedByLandmanUserId = data.receivedByLandmanUserId && data.receivedByLandmanUserId > 0 ? parseInt(data.receivedByLandmanUserId + '', 10) : null;
            data.sentToLandCompanyUserId = data.sentToLandCompanyUserId && data.sentToLandCompanyUserId > 0 ? parseInt(data.sentToLandCompanyUserId + '', 10) : null;
            data.sentToClientUserId = data.sentToClientUserId && data.sentToClientUserId > 0 ? parseInt(data.sentToClientUserId + '', 10) : null;
            data.receivedByDlcUserId = data.receivedByDlcUserId && data.receivedByDlcUserId > 0 ? parseInt(data.receivedByDlcUserId + '', 10) : null;
            data.titleOpinionReceivedUserId = data.titleOpinionReceivedUserId && data.titleOpinionReceivedUserId > 0 ? parseInt(data.titleOpinionReceivedUserId + '', 10) : null;
            data.titleClearedUserId = data.titleClearedUserId && data.titleClearedUserId > 0 ? parseInt(data.titleClearedUserId + '', 10) : null;
            data.clientRequestedUpdateUserId = data.clientRequestedUpdateUserId && data.clientRequestedUpdateUserId > 0 ? parseInt(data.clientRequestedUpdateUserId + '', 10) : null;
            data.updateRequestCompletedUserId = data.updateRequestCompletedUserId && data.updateRequestCompletedUserId > 0 ? parseInt(data.updateRequestCompletedUserId + '', 10) : null;
            data.statusTypeCheckAssignedByUserId = data.statusTypeCheckAssignedByUserId && data.statusTypeCheckAssignedByUserId > 0 ? parseInt(data.statusTypeCheckAssignedByUserId + '', 10) : null;
    
            data.receivedFromClientDate = data.receivedFromClientDate
            ? (moment(data.receivedFromClientDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
            data.assignedToLandmanDate = data.assignedToLandmanDate
            ? (moment(data.assignedToLandmanDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
            data.receivedByLandmanDate = data.receivedByLandmanDate
            ? (moment(data.receivedByLandmanDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
            data.sentToLandCompanyDate = data.sentToLandCompanyDate
            ? (moment(data.sentToLandCompanyDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
            data.sentToClientDate = data.sentToClientDate
            ? (moment(data.sentToClientDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
            data.titleOpinionReceivedDate = data.titleOpinionReceivedDate
            ? (moment(data.titleOpinionReceivedDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
            data.titleClearedDate = data.titleClearedDate
            ? (moment(data.titleClearedDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
            data.receivedByDlcDate = data.receivedByDlcDate
            ? (moment(data.receivedByDlcDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
            data.clientRequestedUpdateDate = data.clientRequestedUpdateDate
            ? (moment(data.clientRequestedUpdateDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
            data.updateRequestCompletedDate = data.updateRequestCompletedDate
            ? (moment(data.updateRequestCompletedDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
            data.onHoldDate = data.onHoldDate
            ? (moment(data.onHoldDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
    
            data.onHold = data.onHold ? data.onHold : false;

            if (data.curativeRequirements?.length > 0) {
                data.curativeRequirements = finalizeCurativeRequirements(data.curativeRequirements);
            }
        }

        return data;
    };

    const finalizeCurativeRequirements = (data: CurativeRequirement[]) => {
        for (let req of data) {
            req.id = !!req.id ? parseInt(req.id + '', 10) : 0;
            req.runsheetId = Number(selectedRunsheet?.id || 0);
            req.statusId = req.statusId > 0 ? parseInt(req.statusId + '', 10) : null;
            req.assignedToId = req.assignedToId > 0 ? parseInt(req.assignedToId + '', 10) : null;

            req.dateAssigned = req.dateAssigned
            ? (moment(req.dateAssigned).format('YYYY-MM-DD') as unknown as Date)
            : null;

            req.isCompleted = AgreementUtil.parseBool(req.isCompleted + '');
        }

        return data;
    };

    return (
        <div style={{justifyContent: 'center', overflow: 'hidden'}} className="dlc-max-width">
            <div style={{overflow: 'hidden', width: '70%'}}>
                <RHFLegend title="Runsheet Administration" />
                <div style={{display: 'flex'}}>
                    <label style={{paddingLeft: '27px', paddingRight: '8px', fontSize: '15px', whiteSpace: 'nowrap'}}>Search Run Sheets</label>
                    <RHFInputBox
                        style={{height: '20px'}}
                        onChange={(event) => runsheetSearch(event.currentTarget.value)}
                        name="runsheetsearch"
                        register={register}
                    />
                </div>
                <div style={{ overflow: 'hidden', display: 'flex' }}>
                    <div className="mr-2" style={{ width: '100%' }}>
                        <div style={{ overflow: 'hidden', display: 'flex', justifyContent: 'space-between' }}>
                            <div style={{ overflow: 'hidden', padding: '1rem', width: '25%' }}>
                                <div><b style={{whiteSpace: 'nowrap'}}>Title Attorney</b></div>
                            </div>
                            <div style={{ padding: '1rem', width: '25%' }}>
                                <div><b style={{whiteSpace: 'nowrap'}}>Client Landman</b></div>
                            </div>
                            <div style={{ padding: '1rem', width: '25%' }}>
                                <div><b style={{whiteSpace: 'nowrap'}}>Well Name</b></div>
                            </div>
                            <div style={{ padding: '1rem', width: '25%' }}>
                                <div><b style={{whiteSpace: 'nowrap'}}>Document Number</b></div>
                            </div>
                        </div>
                    </div>
                </div>
                <Page>
                    <Page.Section>
                        <Panel fitPanelToScreen={true} customSize={1}>
                            <CardContainer
                                getScrollStatus={getRunsheetListScrollStatus}
                                className="clients-page-client-list"
                                showNextPagePanel={fetchNextRunsheetsPage}
                                nextPageAction={incrementRunsheets}
                            >
                                {displayRunsheets()}
                                <div style={{ height: '3rem' }}/>
                            </CardContainer>
                        </Panel>
                    </Page.Section>
                </Page>
                <div style={{ padding: '1rem', display: 'flex', gap: '0.5rem' }}>
                    <Button onClick={addRunsheet} text="Create Runsheet" />
                    <Button
                        onClick={exportRoutingLog}
                        disabled={exportingRoutingLog}
                        text={exportingRoutingLog ? 'Building Excel...' : 'Runsheet Export'}
                        title="Download the Runsheet Routing Log for this project"
                    />
                </div>

                <RunsheetDetailsDialog
                    seededTractSearchTerm={seededTractSearchTerm}
                    selectedTract={selectedTractId}
                    incrementProjectTracts={incrementProjectTracts}
                    runsheet={finalizeData(selectedRunsheet)}
                    users={users}
                    projectTracts={projectTracts}
                    name={DialogList.ADD_RUNSHEET}
                    title={dialogTitle}
                    projectNumber={appContext.projectNumber}
                    clientNumber={appContext.clientNumber}
                    curativeStatuses={curativeStatuses}
                    projectStaff={projectStaff}
                    loadingTracts={loadingTracts}
                    onCloseDialog={hideDialog}
                    finalizeData={finalizeData}
                    finalizeCurativeRequirements={finalizeCurativeRequirements}
                    setProjectTractsSearchTerm={setProjectTractSearchTerm}
                    navigateToTractDetail={navigateToTractDetail}
                />
            </div>
        </div>
    );
};

export default RunsheetPage;



// WEBPACK FOOTER //
// ./src/components/project/sections/RunsheetPage.tsx